// HubTable.tsx

import { useEffect, useState } from "react";
import { MIS_REPORT_URLS } from "../../api/UserUrls";
import { post } from "../../services/smartApiService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

interface headerProps {
  year: any,
  vehicle_type: any
}

const HubVsVichele: React.FC<headerProps> = ({ year, vehicle_type }) => {
  const [data, setData] = useState<any[]>([]);
  
  const loadData = () => {
    let _data = {
      year,
      vehicle_type
    };
    let URL = MIS_REPORT_URLS.GET_VEHICLE_REPORT;
    const subscription = post(URL, _data).subscribe((response) => {
      const rawData = response.data;
      const transformedData: any[] = [];

      rawData.forEach((item: any) => {
        const hubName = item.hub_name;
        const companyName = item.customer_name;
        const vendorCompany = item.vendor_company || ''; 
        const monthIndex = parseInt(item.month) - 1; 
        const vehicleCount = parseInt(item.vehicle_count);

        let hub = transformedData.find(h => h.hubName === hubName);
        if (!hub) {
          hub = {
            hubName,
            monthlyTotal: new Array(12).fill(0),
            companies: [],
            grandTotal: 0
          };
          transformedData.push(hub);
        }

        // Add to monthlyTotal and grandTotal
        hub.monthlyTotal[monthIndex] += vehicleCount;
        hub.grandTotal += vehicleCount;

        // Find or create company entry
        let company = hub.companies.find((c: any) => 
          c.name === companyName && c.vendorCompany === vendorCompany);
        if (!company) {
          company = {
            name: companyName,
            vendorCompany: vendorCompany,
            monthly: new Array(12).fill(0),
            total: 0
          };
          hub.companies.push(company);
        }

        // Add to company monthly data and total
        company.monthly[monthIndex] += vehicleCount;
        company.total += vehicleCount;
      });

      setData(transformedData);
    });

    return () => {
      subscription.unsubscribe();
    };
  };

  useEffect(() => {
    loadData();
  }, [year]);

 const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

const exportHubDataToExcel = (hubData: any, fileName = "hub_report.xlsx") => {
  const excelData: any[] = [];

  hubData.forEach((hub: any) => {
    // Add company rows first
    hub.companies.forEach((company: any) => {
      const companyRow: any = { 
        'Hub': hub.hubName, 
        'Type': company.name,
        'Vendor Company': company.vendorCompany 
      };
      months.forEach((month, i) => {
        companyRow[month] = company.monthly[i] || "";
      });
      excelData.push(companyRow);
    });

    // Add hub total row after all companies
    const totalRow: any = { 
      'Hub': hub.hubName, 
      'Type': 'Total',
      'Vendor Company': '' // Add empty vendor company for alignment
    };
    months.forEach((month, i) => {
      totalRow[month] = hub.monthlyTotal[i] || "";
    });
    excelData.push(totalRow);

    // Add empty row for separation
    excelData.push({});
  });

  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Hub Report");

  // Auto-fit columns
  const wscols = [
    { wch: 30 }, // Hub column
    { wch: 30 }, // Type column
    { wch: 30 }, // Vendor Company column
    ...months.map(() => ({ wch: 10 })) // Month columns
  ];
  worksheet['!cols'] = wscols;

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, fileName);
};

  return (
    <div className="smart-elf-table">
      <div className="is-flex is-justify-content-end mb-3">
        <button
          className="button is-small is-success is-light"
          onClick={() => exportHubDataToExcel(data)}
        >
          <span className="icon is-small"><i className="fa fa-file-excel-o"></i></span>
          <span>Export to Excel</span>
        </button>
      </div>
      <div className="table-container">
        <table className="table is-bordered is-striped is-hoverable is-fullwidth is-size-7 smart-efl-table">
          <thead>
            <tr>
              <th>Hub Name</th>
              <th>Customer Name</th>
              <th>Vendor Company</th>
              {months.map((month, i) => (
                <th key={i}>{month}</th>
              ))}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {data.map((hub, hubIndex) => (
              <>
                {/* Company rows */}
                {hub.companies.map((company: any, compIndex: number) => (
                  <tr key={`comp-${hubIndex}-${compIndex}`}>
                    <td>{compIndex === 0 ? hub.hubName : ''}</td>
                    <td>{company.name}</td>
                    <td>{company.vendorCompany}</td>
                    {company.monthly.map((count: any, i: number) => (
                      <td key={i}>{count !== 0 ? count : ''}</td>
                    ))}
                    <td className="has-text-weight-bold">{company.total}</td>
                  </tr>
                ))}
                
                {/* Hub total row */}
                {hub.companies.length > 0 && (
                  <tr className="has-background-light" key={`total-${hubIndex}`}>
                    <td></td>
                    <td className="has-text-weight-bold">TOTAL</td>
                    <td></td>
                    {hub.monthlyTotal.map((count: any, i: number) => (
                      <td key={i} className="has-text-weight-bold">
                        {count !== 0 ? count : ''}
                      </td>
                    ))}
                    <td className="has-text-weight-bold">{hub.grandTotal}</td>
                  </tr>
                )}
                
                {/* Empty row for spacing */}
                <tr key={`spacer-${hubIndex}`} className="is-hidden-mobile">
                  <td colSpan={15} style={{ height: '1rem', backgroundColor: '#f5f5f5' }}></td>
                </tr>
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HubVsVichele;